import { EditOutlined } from '@ant-design/icons';
import { ConnectError } from '@bufbuild/connect';
import { Button, Input, notification, Typography } from 'antd';
import cn from 'classnames';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAppDispatch } from '@/shared/hooks';

import { organizationModel } from '@/entities/organization';

import styles from './OrganizationComment.module.scss';

const { TextArea } = Input;
const { Text } = Typography;

type OrganizationCommentProps = {
  comment: string;
  organizationID: string;
  canManageComment: boolean;
};

export const OrganizationComment: FC<OrganizationCommentProps> = (props) => {
  const { comment, organizationID, canManageComment } = props;

  const dispatch = useAppDispatch();

  const { formatMessage } = useIntl();

  const [isCommentEditorVisible, setIsCommentEditorVisible] =
    useState<boolean>(false);
  const [newComment, setNewComment] = useState<string>(comment);
  const [loading, setLoading] = useState<boolean>(false);

  const handleCancel = () => {
    setIsCommentEditorVisible(false);
  };

  const handleUpdateComment = async () => {
    setLoading(true);

    try {
      const { Organization } = await dispatch(
        organizationModel.thunks.setOrganizationComment({
          OrganizationID: organizationID,
          Comment: newComment,
        }),
      ).unwrap();

      dispatch(organizationModel.actions.setNewestOrganization(Organization));
      handleCancel();
    } catch (error) {
      const parsedMessage = JSON.parse((error as ConnectError)?.rawMessage);

      notification.error({
        message:
          parsedMessage?.message ??
          parsedMessage?.text ??
          parsedMessage?.reason ??
          formatMessage({
            id: 'global.error.somethingWentWrong',
            defaultMessage: 'Something went wrong',
          }),
      });
    } finally {
      setLoading(false);
    }
  };

  const isAddButtonOnly =
    !isCommentEditorVisible && !comment && canManageComment;

  if (!comment && !canManageComment && !isCommentEditorVisible) {
    return null;
  }

  return (
    <>
      <span className={cn(styles.label, isAddButtonOnly && styles.centered)}>
        {formatMessage({
          id: 'organization.comment.label',
          defaultMessage: 'Comment',
        })}
      </span>
      <div className={cn(styles.value, isAddButtonOnly && styles.centered)}>
        {!isCommentEditorVisible &&
          (!comment ? (
            canManageComment && (
              <Button
                type="primary"
                onClick={() => setIsCommentEditorVisible(true)}
              >
                {formatMessage({
                  id: 'organization.comment.button.addComment',
                  defaultMessage: 'Add comment',
                })}
              </Button>
            )
          ) : (
            <span className={styles.editableValue}>
              <Text>{comment}</Text>

              {canManageComment && (
                <EditOutlined
                  className={styles.editIcon}
                  onClick={() => setIsCommentEditorVisible(true)}
                  rev={undefined}
                />
              )}
            </span>
          ))}

        {isCommentEditorVisible && (
          <div className={styles.textAreaContainer}>
            <TextArea
              value={newComment}
              autoSize={{ minRows: 3, maxRows: 5 }}
              onChange={(event) => setNewComment(event.target.value)}
            />

            <div className={styles.textAreaButtons}>
              <Button onClick={handleCancel} loading={loading}>
                {formatMessage({
                  id: 'global.cancel',
                  defaultMessage: 'Cancel',
                })}
              </Button>

              <Button
                type="primary"
                onClick={handleUpdateComment}
                loading={loading}
              >
                {formatMessage({ id: 'global.save', defaultMessage: 'Save' })}
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
